import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subscription, throwError, timer } from 'rxjs';
import { finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { REFRESH_TOKEN } from '../constants/api-endpoints';
import { RefreshTokenResponse } from '../../models/common/login';
import { BaseSessionService } from './base-session.service';
import { NotificationService } from './notification.service';

/** The backend rejects a refresh once the access token has expired, so refresh this far ahead. */
const REFRESH_LEAD_MS = 5 * 60 * 1000;

/** The only text a user sees when a session ends, whatever the cause. */
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

/** The refresh call reached the server but could not renew the session for a reason
 *  other than "this session is over" (a server or configuration problem). */
export class RefreshUnavailableError extends Error {}

/** True only when a failed refresh means the session is really finished: nothing to
 *  refresh with, or the server rejected the refresh token (401). A 404, 5xx, timeout
 *  or offline error is not — those must not sign anyone out. */
export function isSessionEnded(err: unknown): boolean {
  if (err instanceof HttpErrorResponse) {
    return err.status === 401;
  }
  return err instanceof Error && !(err instanceof RefreshUnavailableError) && err.message === SESSION_EXPIRED_MESSAGE;
}

@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly session = inject(BaseSessionService);
  private readonly notify = inject(NotificationService);

  /** Shared so concurrent callers ride one POST — each call burns the server's MaxRefreshCount. */
  private inFlight$: Observable<string> | null = null;
  private timerSub?: Subscription;
  private expiring = false;
  private lifecycleBound = false;

  refreshToken(): Observable<string> {
    if (this.inFlight$) {
      return this.inFlight$;
    }

    const refreshToken = this.session.getSessionItem(BaseSessionService.KEY_REFRESH_TOKEN);
    if (!refreshToken) {
      return throwError(() => new Error(SESSION_EXPIRED_MESSAGE));
    }

    this.inFlight$ = this.http
      .post<RefreshTokenResponse>(REFRESH_TOKEN('tsp'), { refreshToken })
      .pipe(
        map((response) => {
          const token = response?.data?.token;
          if (!token) {
            const status = (response as { statusCode?: number } | undefined)?.statusCode;
            if (status !== undefined && status !== 401) {
              throw new RefreshUnavailableError(`Token refresh failed (${status}).`);
            }
            throw new Error(SESSION_EXPIRED_MESSAGE);
          }
          sessionStorage.setItem(BaseSessionService.KEY_AUTH_TOKEN, token);
          if (response.data?.refreshToken) {
            sessionStorage.setItem(BaseSessionService.KEY_REFRESH_TOKEN, response.data.refreshToken);
          }

          this.scheduleRefresh();
          return token;
        }),
        finalize(() => (this.inFlight$ = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.inFlight$;
  }

  /** Arms a refresh for just before the current token expires, so idle sessions survive too. */
  scheduleRefresh(): void {
    this.timerSub?.unsubscribe();

    // Signed out — nothing to refresh.
    if (!this.session.getSessionItem(BaseSessionService.KEY_AUTH_TOKEN)) {
      return;
    }

    const expiresAt = this.tokenExpiresAt();
    if (expiresAt === null || expiresAt <= Date.now()) {
      return;
    }

    const refreshAt = expiresAt - REFRESH_LEAD_MS;
    const delayMs = Math.max(refreshAt - Date.now(), 0);

    this.timerSub = timer(delayMs)
      .pipe(switchMap(() => this.refreshToken()))
      // Anything short of "the session is over" is left to the next request,
      // which retries the refresh.
      .subscribe({
        error: (err) => {
          if (isSessionEnded(err)) {
            this.expireSession();
          }
        }
      });

    this.bindLifecycleRefresh();
  }

  /** Timers are throttled in background tabs and stop while a machine sleeps, so
   *  the scheduled refresh can be missed. Coming back to the tab re-checks the
   *  token and renews it before the next navigation or request needs it. */
  private bindLifecycleRefresh(): void {
    if (this.lifecycleBound || typeof document === 'undefined') {
      return;
    }
    this.lifecycleBound = true;

    const onReturn = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (!this.session.getSessionItem(BaseSessionService.KEY_AUTH_TOKEN) || !this.shouldRefreshToken()) {
        return;
      }
      this.refreshToken().subscribe({
        error: (err) => {
          if (isSessionEnded(err)) {
            this.expireSession();
          }
        }
      });
    };

    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    window.addEventListener('pageshow', onReturn);
    window.addEventListener('online', onReturn);
  }

  /** Once per page, however many requests fail together. Local sign-out only —
   *  the expired session has nothing left to revoke server-side. */
  expireSession(): void {
    if (this.expiring) {
      return;
    }
    this.expiring = true;
    this.timerSub?.unsubscribe();
    this.notify.warn(SESSION_EXPIRED_MESSAGE, 'Session expired');
    this.notify.hold();
    void this.session.expireSessionAndReload('/login', SESSION_EXPIRED_MESSAGE);
  }

  ngOnDestroy(): void {
    this.timerSub?.unsubscribe();
  }

  shouldRefreshToken(): boolean {
    const expiresAt = this.tokenExpiresAt();
    return expiresAt !== null && expiresAt - Date.now() < REFRESH_LEAD_MS;
  }

  private tokenExpiresAt(): number | null {
    try {
      const token = this.session.getSessionItem(BaseSessionService.KEY_AUTH_TOKEN);
      const exp = token ? jwtDecode(token).exp : undefined;
      return exp ? exp * 1000 : null;
    } catch {
      return null;
    }
  }
}
